import Vue from 'vue';
import Vuex from 'vuex';
import router from '@/router';
import axios from '@/api';
import jwtDecode from 'jwt-decode';
import { askNotificationsPermissions } from '@/push-notification';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    usuario: null,
    loading: false,
    accessToken: null,
    refreshToken: null,
    refreshTimeout: null,
    isLoadingRoute: false,
  },
  mutations: {
    authUsuario(state, dadosAutenticao) {
      state.accessToken = dadosAutenticao.accessToken;
      state.refreshToken = dadosAutenticao.refreshToken;
      state.usuario = {
        login: dadosAutenticao.usuario.login,
        id: dadosAutenticao.usuario.id,
        nome: dadosAutenticao.usuario.nome,
      };
    },
    clearUsuarioData(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.usuario = null;
      clearTimeout(state.refreshTimeout);
      state.refreshTimeout = null;
    },
    refreshTask(state, refreshTimeout) {
      clearTimeout(state.refreshTimeout);
      state.refreshTimeout = refreshTimeout;
    },
    loadingRouteStart(state) {
      state.isLoadingRoute = true;
    },
    loadingRouteEnd(state) {
      state.isLoadingRoute = false;
    },
  },
  actions: {
    auth({ commit, dispatch }, authData) {
      const url = authData.authType === 'login' ? '/auth/login' : '/auth/signup';
      return new Promise((resolve, reject) => {
        axios
          .post(url, {
            ...authData.usuario,
            hcaptchaToken: authData.hcaptchaToken,
          })
          .then(async (res) => {
            const decodedAcessToken = jwtDecode(res.data.access_token);
            const decodedRefreshToken = jwtDecode(res.data.refresh_token);
            const usuario = {
              id: decodedAcessToken.identity,
              login: decodedAcessToken.user_claims.login,
              nome: decodedAcessToken.user_claims.nome,
            };
            commit('authUsuario', {
              accessToken: res.data.access_token,
              refreshToken: res.data.refresh_token,
              usuario,
            });
            const now = new Date();
            const accessTokenExpDate = new Date(decodedAcessToken.exp * 1000);
            const refreshTokenExpDate = new Date(
              decodedRefreshToken.exp * 1000,
            );
            const timeUntilRefresh = new Date(
              (decodedAcessToken.exp - 10 * 60) * 1000,
            );

            localStorage.setItem('access_token', res.data.access_token);
            localStorage.setItem('refresh_token', res.data.refresh_token);
            localStorage.setItem('usuario', JSON.stringify(usuario));
            localStorage.setItem('accessTokenExpDate', accessTokenExpDate);
            localStorage.setItem('refreshTokenExpDate', refreshTokenExpDate);

            dispatch('autoRefresh', timeUntilRefresh.getTime() - now.getTime());
            askNotificationsPermissions();
            resolve(res);
          })
          .catch((error) => {
            dispatch('clearStoreAndLocalStorage');
            reject(error);
          });
      });
    },
    socialLogin({ commit, dispatch }, idToken) {
      const url = '/auth/social_login';
      const data = {
        token: idToken,
      };
      return new Promise((resolve, reject) => {
        axios
          .post(url, {
            ...data,
          })
          .then(async (res) => {
            const decodedAcessToken = jwtDecode(res.data.access_token);
            const decodedRefreshToken = jwtDecode(res.data.refresh_token);
            const usuario = {
              id: decodedAcessToken.identity,
              login: decodedAcessToken.user_claims.login,
              nome: decodedAcessToken.user_claims.nome,
            };
            commit('authUsuario', {
              accessToken: res.data.access_token,
              refreshToken: res.data.refresh_token,
              usuario,
            });
            const now = new Date();
            const accessTokenExpDate = new Date(decodedAcessToken.exp * 1000);
            const refreshTokenExpDate = new Date(
              decodedRefreshToken.exp * 1000,
            );
            const timeUntilRefresh = new Date(
              (decodedAcessToken.exp - 10 * 60) * 1000,
            );

            localStorage.setItem('access_token', res.data.access_token);
            localStorage.setItem('refresh_token', res.data.refresh_token);
            localStorage.setItem('usuario', JSON.stringify(usuario));
            localStorage.setItem('accessTokenExpDate', accessTokenExpDate);
            localStorage.setItem('refreshTokenExpDate', refreshTokenExpDate);

            dispatch('autoRefresh', timeUntilRefresh.getTime() - now.getTime());
            askNotificationsPermissions();
            resolve(res);
          })
          .catch((error) => {
            dispatch('clearStoreAndLocalStorage');
            reject(error);
          });
      });
    },
    async autoLogin({ commit, dispatch }) {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      if (!(accessToken && refreshToken)) return;
      const accessTokenExpDate = new Date(
        localStorage.getItem('accessTokenExpDate'),
      );
      const refreshTokenExpDate = new Date(
        localStorage.getItem('refreshTokenExpDate'),
      );
      const now = new Date();
      if (!refreshTokenExpDate || now > refreshTokenExpDate) return;
      const usuario = JSON.parse(localStorage.getItem('usuario'));
      if (!(usuario && usuario.id)) return;
      commit('authUsuario', {
        accessToken,
        refreshToken,
        usuario,
      });
      if (!accessTokenExpDate || now > accessTokenExpDate) await dispatch('autoRefresh', 0);
      const delay = accessTokenExpDate.getTime() - now.getTime();
      dispatch('autoRefresh', Math.max(delay, 0));
    },
    refreshTokens({ state, dispatch }) {
      axios
        .post(
          '/refresh',
          {},
          { headers: { Authorization: `Bearer ${state.refreshToken}` } },
        )
        .then((res) => {
          const decodedAcessToken = jwtDecode(res.data.access_token);
          const usuario = {
            id: decodedAcessToken.identity,
            login: decodedAcessToken.user_claims.login,
          };

          const now = new Date();
          const accessTokenExpDate = new Date(decodedAcessToken.exp * 1000);
          const timeUntilRefresh = new Date(
            (decodedAcessToken.exp - 10 * 60) * 1000,
          );

          localStorage.setItem('access_token', res.data.access_token);
          localStorage.setItem('usuario', JSON.stringify(usuario));
          localStorage.setItem('accessTokenExpDate', accessTokenExpDate);

          dispatch('autoRefresh', timeUntilRefresh.getTime() - now.getTime());
        })
        .catch(() => {
          dispatch('logout');
        });
    },
    clearStoreAndLocalStorage({ commit }) {
      commit('clearUsuarioData');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token');
      localStorage.removeItem('accessTokenExpDate');
      localStorage.removeItem('usuario');
    },
    logout({ dispatch }) {
      dispatch('clearStoreAndLocalStorage');
      router.push({ name: 'login' });
    },
    autoRefresh(context, timeUntilRefresh) {
      const { commit, dispatch } = context;
      const promise = new Promise((resolve) => {
        const refreshTimeout = setTimeout(() => {
          dispatch('refreshTokens');
          resolve();
        }, timeUntilRefresh);
        commit('refreshTask', refreshTimeout); // In case you want to cancel this task on logout
      });
      return promise;
    },
  },
  getters: {
    usuarioId(state) {
      return state.usuario.id;
    },
    usuario(state) {
      return state.usuario;
    },
    isAuthenticated(state) {
      return state.accessToken != null;
    },
    isLoadingRoute(state) {
      return state.isLoadingRoute;
    },
  },
});
